type Direction = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight';
type ReadyElement = HTMLElement & { componentOnReady?: () => Promise<unknown>; disabled?: boolean };

const controlSelector = '[data-remote-focus]';
const overlaySelector = 'ion-modal, ion-alert, ion-popover, ion-action-sheet, ion-loading, ion-picker';

/** Adds directional focus without replacing native keyboard activation or tab order. */
export class RemoteNavigation {
  private preferredTestId: string | null = null;
  private focusRequest = 0;
  private cancelRenderWait?: () => void;

  constructor(private readonly root: HTMLElement) {}

  moveFocus(event: KeyboardEvent): void {
    if (!this.isDirection(event.key) || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey ||
        !this.isActiveScope() || this.isEditing(event)) {
      return;
    }

    const controls = this.getControls();
    if (!controls.length) return;
    const current = controls.find(control => event.composedPath().includes(control)) ?? this.getFocusedControl(controls);
    event.preventDefault();
    if (!current) {
      void this.restoreFocus();
      return;
    }

    const target = this.getDirectionalTarget(current, controls, event.key);
    if (target) void this.focusControl(target);
  }

  async restoreFocus(): Promise<void> {
    if (!this.isActiveScope()) return;
    const controls = this.getControls();
    // Keep focus selected by pointer, Tab, or Ionic's own overlay restoration.
    if (this.getFocusedControl(controls)) return;
    const target = controls.find(control => control.getAttribute('data-testid') === this.preferredTestId) ?? controls[0];
    if (target) await this.focusControl(target);
  }

  async focusTarget(testId: string): Promise<void> {
    if (!this.isActiveScope()) return;
    const target = this.getControls().find(control => control.getAttribute('data-testid') === testId);
    if (target) await this.focusControl(target);
  }

  rememberFocus(event: FocusEvent): void {
    const control = this.getControls().find(candidate => event.composedPath().includes(candidate));
    if (control) {
      this.cancelPendingFocus();
      this.preferredTestId = control.getAttribute('data-testid');
    }
  }

  cancelPendingFocus(): void {
    this.focusRequest++;
    this.cancelRenderWait?.();
    this.cancelRenderWait = undefined;
  }

  private isDirection(key: string): key is Direction {
    return ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key);
  }

  isActiveScope(): boolean {
    if (!this.root.isConnected || this.root.closest('.ion-page-hidden, [hidden], [inert], [aria-hidden="true"]')) return false;
    const overlays = Array.from(this.root.ownerDocument.querySelectorAll<HTMLElement>(overlaySelector));
    const topOverlay = overlays.filter(overlay => !overlay.classList.contains('overlay-hidden') &&
      !overlay.hidden && overlay.getAttribute('aria-hidden') !== 'true').pop();
    return !topOverlay || topOverlay.contains(this.root);
  }

  private isEditing(event: KeyboardEvent): boolean {
    return event.composedPath().some(target => target instanceof HTMLElement &&
      (target.matches('input, textarea, select, ion-input, ion-textarea, ion-select') || target.isContentEditable));
  }

  private getControls(): ReadyElement[] {
    return Array.from(this.root.querySelectorAll<ReadyElement>(controlSelector)).filter(control =>
      !control.disabled && !control.hasAttribute('disabled') && control.getAttribute('aria-disabled') !== 'true' &&
      !control.closest('[hidden], [inert], [aria-hidden="true"]') && control.getClientRects().length > 0 &&
      getComputedStyle(control).visibility === 'visible');
  }

  private getFocusedControl(controls: HTMLElement[]): HTMLElement | undefined {
    return controls.find(control => control === this.root.ownerDocument.activeElement || control.matches(':focus-within'));
  }

  private getDirectionalTarget(current: HTMLElement, controls: HTMLElement[], direction: Direction): HTMLElement | undefined {
    const rect = current.getBoundingClientRect();
    const horizontal = direction === 'ArrowLeft' || direction === 'ArrowRight';
    const sign = direction === 'ArrowLeft' || direction === 'ArrowUp' ? -1 : 1;
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    return controls.filter(control => control !== current).map(control => {
      const candidate = control.getBoundingClientRect();
      const dx = candidate.left + candidate.width / 2 - x;
      const dy = candidate.top + candidate.height / 2 - y;
      // Compare the current focus point to the candidate's full span so wide
      // settings remain aligned with a corner action such as a dialog close button.
      const cross = horizontal
        ? Math.max(candidate.top - y, y - candidate.bottom, 0)
        : Math.max(candidate.left - x, x - candidate.right, 0);
      return { control, forward: (horizontal ? dx : dy) * sign, cross };
    }).filter(candidate => candidate.forward > 1)
      .sort((a, b) => (a.cross * 4 + a.forward) - (b.cross * 4 + b.forward))[0]?.control;
  }

  private async focusControl(control: ReadyElement): Promise<void> {
    this.cancelPendingFocus();
    const request = this.focusRequest;
    if (control.componentOnReady) await control.componentOnReady();
    if (request !== this.focusRequest) return;
    const nativeControl = await this.waitForNativeControl(control);
    if (!nativeControl) return;
    if (request !== this.focusRequest || !this.isActiveScope() || !this.getControls().includes(control)) return;
    nativeControl.focus({ preventScroll: true });
    control.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    this.preferredTestId = control.getAttribute('data-testid');
  }

  private waitForNativeControl(control: HTMLElement): Promise<HTMLElement | undefined> {
    const shadow = control.shadowRoot;
    if (!shadow) return Promise.resolve(control);
    const selector = 'button, a, input, [tabindex]';
    const existing = shadow.querySelector<HTMLElement>(selector);
    if (existing) return Promise.resolve(existing);

    // Standalone Ionic elements do not expose componentOnReady. Observe their
    // shadow content instead of relying on a timeout or focusing an inert host.
    return new Promise(resolve => {
      const finish = (target?: HTMLElement): void => {
        observer.disconnect();
        this.cancelRenderWait = undefined;
        resolve(target);
      };
      const observer = new MutationObserver(() => {
        const target = shadow.querySelector<HTMLElement>(selector);
        if (target) finish(target);
      });
      this.cancelRenderWait = () => finish();
      observer.observe(shadow, { childList: true, subtree: true });
    });
  }

}
